import React, { useState, useMemo } from 'react';
import {
  proRata,
  METHODS,
  METHOD_LABELS,
  METHOD_DESCRIPTIONS,
  DEFAULT_METHOD,
  monthBounds,
  toIsoDate,
} from '../utils/proRataCalc';
import DateInput from './DateInput';
import InfoTooltip from './InfoTooltip';

// Pro-rata calculator UI. Use for new starters (provide employmentStart) or
// leavers (provide employmentEnd).
export default function ProRataHelper({
  annualSalary,
  employmentStart,
  employmentEnd,
  initialMethod,
  onApply,
  label,
}) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState(initialMethod || DEFAULT_METHOD);
  const [customDivisor, setCustomDivisor] = useState('260');

  const anchorDate = employmentStart || employmentEnd;
  const defaultPeriod = useMemo(() => monthBounds(anchorDate || new Date()), [anchorDate]);
  const [periodStart, setPeriodStart] = useState(toIsoDate(defaultPeriod.start));
  const [periodEnd, setPeriodEnd] = useState(toIsoDate(defaultPeriod.end));

  const result = useMemo(() => {
    if (!annualSalary || parseFloat(annualSalary) <= 0) return null;
    if (!periodStart || !periodEnd) return null;
    return proRata({
      annualSalary,
      method,
      periodStart,
      periodEnd,
      employmentStart,
      employmentEnd,
      customDivisor: method === METHODS.CUSTOM ? customDivisor : undefined,
    });
  }, [annualSalary, method, periodStart, periodEnd, employmentStart, employmentEnd, customDivisor]);

  const annualValid = annualSalary && parseFloat(annualSalary) > 0;
  const dateValid = !!anchorDate;

  return (
    <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/30">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label || 'Pro-rata calculator'}
          {result && (
            <span className="ml-2 text-brand-700 dark:text-brand-400 font-semibold">
              {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(result.amount)}
            </span>
          )}
        </span>
        <span className="text-gray-400 text-xs">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {!annualValid && (
            <p className="text-xs text-amber-700 dark:text-amber-400">Enter an annual salary above to calculate.</p>
          )}
          {!dateValid && (
            <p className="text-xs text-amber-700 dark:text-amber-400">Enter a start or leaving date above to calculate.</p>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Method
              <InfoTooltip text="Different employment contracts use different conventions. Pick the one that matches the contract." />
            </label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full text-sm rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              {Object.entries(METHOD_LABELS).map(([value, lbl]) => (
                <option key={value} value={value}>{lbl}</option>
              ))}
            </select>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{METHOD_DESCRIPTIONS[method]}</p>
          </div>

          {method === METHODS.CUSTOM && (
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Divisor</label>
              <input
                type="number"
                min="1"
                value={customDivisor}
                onChange={(e) => setCustomDivisor(e.target.value)}
                className="w-32 text-sm rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Period start</label>
              <DateInput name="period_start" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
                className="w-full text-sm rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Period end</label>
              <DateInput name="period_end" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                className="w-full text-sm rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>
          </div>

          {result && (
            <div className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Pro-rata pay: {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(result.amount)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{result.formula}</p>
              {onApply && result.amount > 0 && (
                <button
                  type="button"
                  onClick={() => onApply(result)}
                  className="mt-2 text-xs px-2 py-1 bg-brand-600 text-white rounded hover:bg-brand-700"
                >
                  Use this figure
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
